// IVR Points of Interest -- local landmarks/gate entrances/cab stands the
// IVR's own address matching uses because generic geocoding gets them
// wrong (e.g. "Hospital Main Entry" only geocodes to the helipad in
// Google). Seeded from a one-off CSV export of the IVR's own POI list
// (migration 0052); `cat` and `zid` are that system's own opaque
// category/zone codes, kept as-is rather than guessed at.
import { supabase } from "@/lib/supabase";

export interface IvrPoi {
  id: string;
  tenant_id: string;
  name: string;
  lon: number;
  lat: number;
  cat: number;
  zid: number;
  flags: number;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export async function fetchIvrPois(): Promise<IvrPoi[]> {
  const { data, error } = await supabase.from("ivr_pois").select("*").order("name");
  if (error) {
    console.error("Error fetching IVR POIs:", error);
    return [];
  }
  return data || [];
}

export async function createIvrPoi(
  input: Pick<IvrPoi, "name" | "lon" | "lat" | "cat" | "zid" | "flags"> & Partial<Pick<IvrPoi, "notes" | "is_active">>
): Promise<IvrPoi> {
  const { data, error } = await supabase.from("ivr_pois").insert(input).select().single();
  if (error) throw error;
  return data;
}

export async function updateIvrPoi(id: string, patch: Partial<IvrPoi>): Promise<void> {
  const { error } = await supabase
    .from("ivr_pois")
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

export async function deleteIvrPoi(id: string): Promise<void> {
  const { error } = await supabase.from("ivr_pois").delete().eq("id", id);
  if (error) throw error;
}

// Used to fold POIs into address-search dropdowns (e.g. CRM Home Address)
// alongside Mapbox results -- these are the hand-pinned landmarks (rest home
// entrances, etc.) that generic geocoding gets wrong, so a name match here
// should surface before/alongside Mapbox's own guess.
export async function searchIvrPois(query: string, limit = 6): Promise<IvrPoi[]> {
  const term = query.trim();
  if (!term) return [];
  const { data, error } = await supabase
    .from("ivr_pois")
    .select("*")
    .eq("is_active", true)
    .ilike("name", `%${term}%`)
    .order("name")
    .limit(limit);
  if (error) {
    console.error("Error searching IVR POIs:", error);
    return [];
  }
  return data || [];
}
